import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  BookOpen,
  Calendar,
  ChevronRight,
  Eye,
  Facebook,
  Globe,
  Heart,
  Home,
  Image as ImageIcon,
  Instagram,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  PlayCircle,
  Send,
  Sparkles,
  User,
  Youtube,
  X,
} from "lucide-react";

const API_BASE = "https://panel.haymestasarim.com/api";
const API_ILETISIM = `${API_BASE}/iletisim/public`;

function detectLang() {
  const l = navigator.language?.slice(0, 2);
  return ["tr", "en", "de"].includes(l) ? l : "tr";
}

const TEXT = {
  tr: {
    title: "Dijital Anıtlar",
    subtitle: "Sevdiklerinizin hatırasını yaşatan özel anı sayfası",
    loading: "Yükleniyor...",
    noData: "Henüz anıt bulunmuyor",
    backHome: "Ana Sayfa",
    detail: "Anıtı Gör",
    born: "Doğum",
    died: "Vefat",
    cemetery: "Mezarlık",
    graveNo: "Mezar No",
    city: "Şehir",
    views: "Görüntülenme",
    messages: "Mesaj",
    media: "Medya",
    life: "Hayat Hikayesi",
    religious: "Dini İçerikler",
    gallery: "Galeri ve Anılar",
    condolences: "Anı Defteri",
    location: "Mezar Konumu",
    familyNote: "Aile Notu",
    sendMessage: "Taziye Mesajı Gönder",
    name: "Adınız Soyadınız",
    message: "Mesajınız",
    messagePlaceholder:
      "Merhuma Allah'tan rahmet, ailesine sabır ve başsağlığı dilerim...",
    send: "Gönder",
    sent: "Mesajınız gönderildi.",
    error: "Bir hata oluştu.",
    emptyLife: "Henüz hayat hikayesi eklenmemiş.",
    emptyReligious: "Henüz dini içerik eklenmemiş.",
    emptyGallery: "Henüz galeri/anı eklenmemiş.",
    emptyCondolence: "Henüz mesaj yok. İlk mesajı siz yazın.",
    contact: "İletişim",
    follow: "Bizi Takip Edin",
    mapOpen: "Konuma Git",
    readMore: "Devamını Oku",
    readLess: "Kapat",
    watchOnYouTube: "Videoyu İzle",
  },
  en: {
    title: "Digital Memorials",
    subtitle:
      "A special memorial page preserving the memory of your loved ones",
    loading: "Loading...",
    noData: "No memorials found",
    backHome: "Home",
    detail: "View Memorial",
    born: "Born",
    died: "Died",
    cemetery: "Cemetery",
    graveNo: "Grave No",
    city: "City",
    views: "Views",
    messages: "Messages",
    media: "Media",
    life: "Life Story",
    religious: "Religious Content",
    gallery: "Gallery & Memories",
    condolences: "Guest Book",
    location: "Grave Location",
    familyNote: "Family Note",
    sendMessage: "Send Condolence",
    name: "Your Full Name",
    message: "Your Message",
    messagePlaceholder:
      "May God have mercy on the deceased and give patience to the family...",
    send: "Send",
    sent: "Your message has been sent.",
    error: "An error occurred.",
    emptyLife: "No life story has been added yet.",
    emptyReligious: "No religious content has been added yet.",
    emptyGallery: "No gallery/memory has been added yet.",
    emptyCondolence: "No messages yet. Be the first to write.",
    contact: "Contact",
    follow: "Follow Us",
    mapOpen: "Open Location",
    readMore: "Read More",
    readLess: "Close",
    watchOnYouTube: "Watch Video",
  },
  de: {
    title: "Digitale Denkmäler",
    subtitle: "Eine besondere Gedenkseite für Ihre Liebsten",
    loading: "Laden...",
    noData: "Keine Denkmäler gefunden",
    backHome: "Startseite",
    detail: "Denkmal ansehen",
    born: "Geboren",
    died: "Verstorben",
    cemetery: "Friedhof",
    graveNo: "Grab Nr.",
    city: "Stadt",
    views: "Aufrufe",
    messages: "Nachrichten",
    media: "Medien",
    life: "Lebensgeschichte",
    religious: "Religiöse Inhalte",
    gallery: "Galerie & Erinnerungen",
    condolences: "Gästebuch",
    location: "Grabstandort",
    familyNote: "Familiennotiz",
    sendMessage: "Beileid senden",
    name: "Ihr vollständiger Name",
    message: "Ihre Nachricht",
    messagePlaceholder:
      "Möge Gott dem Verstorbenen gnädig sein und der Familie Geduld geben...",
    send: "Senden",
    sent: "Ihre Nachricht wurde gesendet.",
    error: "Ein Fehler ist aufgetreten.",
    emptyLife: "Noch keine Lebensgeschichte hinzugefügt.",
    emptyReligious: "Noch keine religiösen Inhalte hinzugefügt.",
    emptyGallery: "Noch keine Galerie/Erinnerung hinzugefügt.",
    emptyCondolence: "Noch keine Nachrichten vorhanden.",
    contact: "Kontakt",
    follow: "Folgen Sie uns",
    mapOpen: "Standort öffnen",
    readMore: "Mehr lesen",
    readLess: "Schließen",
    watchOnYouTube: "Video ansehen",
  },
};

function safeArray(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function resolveImage(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  if (url.startsWith("/")) return `https://panel.haymestasarim.com${url}`;
  return `https://panel.haymestasarim.com/${url}`;
}

function formatDate(text, picker, lang) {
  if (text) return text;
  if (!picker) return "-";
  try {
    return new Date(picker).toLocaleDateString(
      lang === "de" ? "de-DE" : lang === "en" ? "en-US" : "tr-TR",
    );
  } catch {
    return "-";
  }
}

function getYouTubeEmbedUrl(url) {
  if (!url) return null;
  const reg = /^.*(youtu.be\/|watch\?v=|embed\/|shorts\/)([^#&?]*).*/;
  const match = url.match(reg);
  const id = match && match[2]?.length === 11 ? match[2] : null;
  if (id) return `https://www.youtube.com/embed/${id}?autoplay=1`;
  if (url.includes("youtube.com/embed/")) return url;
  return null;
}

const APP_STYLES = `
  :root {
    --memorial-navy: #071a33;
    --memorial-blue: #0b2d55;
    --memorial-blue-light: #174a7e;
    --memorial-gold: #d6a84b;
    --memorial-gold-light: #f2d58e;
    --memorial-ink: #142033;
    --memorial-muted: #657187;
    --memorial-line: #dfe6ef;
    --memorial-surface: #ffffff;
    --memorial-bg: #f3f6fa;
    --memorial-success: #15966d;
    --memorial-shadow: 0 18px 50px rgba(7, 26, 51, 0.1);
    --memorial-shadow-soft: 0 8px 28px rgba(7, 26, 51, 0.07);
  }

  * {
    box-sizing: border-box;
  }

  html {
    scroll-behavior: smooth;
  }

  body {
    margin: 0;
    background: var(--memorial-bg);
  }

  button,
  input,
  textarea {
    font: inherit;
  }

  button,
  a {
    -webkit-tap-highlight-color: transparent;
  }

  .app-container {
    min-height: 100vh;
    overflow-x: hidden;
    background:
      radial-gradient(circle at 8% 38%, rgba(23, 74, 126, 0.06), transparent 24rem),
      var(--memorial-bg);
    color: var(--memorial-ink);
    font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  }

  .container {
    width: min(100% - 48px, 1280px);
    max-width: none;
    margin-inline: auto;
    padding: 0;
  }

  .home-header,
  .detail-header {
    position: relative;
    isolation: isolate;
    overflow: hidden;
    color: #fff;
    background:
      radial-gradient(circle at 82% 10%, rgba(53, 119, 188, 0.46), transparent 29rem),
      radial-gradient(circle at 12% 105%, rgba(214, 168, 75, 0.16), transparent 21rem),
      linear-gradient(118deg, #06172e 0%, #0a2a50 54%, #123f70 100%);
  }

  .home-header::before,
  .detail-header::before {
    content: "";
    position: absolute;
    z-index: -1;
    inset: 0;
    background-image:
      linear-gradient(rgba(255, 255, 255, 0.025) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255, 255, 255, 0.025) 1px, transparent 1px);
    background-size: 46px 46px;
    mask-image: linear-gradient(to bottom, black, transparent 92%);
  }

  .home-header::after,
  .detail-header::after {
    content: "";
    position: absolute;
    right: -7rem;
    bottom: -12rem;
    z-index: -1;
    width: 31rem;
    height: 31rem;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 50%;
    box-shadow:
      0 0 0 3.5rem rgba(255, 255, 255, 0.025),
      0 0 0 7rem rgba(255, 255, 255, 0.018);
  }

  .home-header {
    min-height: 430px;
    padding: 28px 0 112px;
  }

  .detail-header {
    padding: 26px 0 104px;
  }

  .header-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin: 0 0 54px;
  }

  .back-button {
    min-height: 44px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 9px;
    padding: 10px 17px;
    border: 1px solid rgba(255, 255, 255, 0.17);
    border-radius: 13px;
    background: rgba(255, 255, 255, 0.08);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08);
    color: #fff;
    font-size: 14px;
    font-weight: 750;
    text-decoration: none;
    cursor: pointer;
    backdrop-filter: blur(12px);
    transition: 180ms ease;
  }

  .back-button:hover {
    border-color: rgba(255, 255, 255, 0.3);
    background: rgba(255, 255, 255, 0.14);
    transform: translateY(-1px);
  }

  .lang-switch {
    display: flex;
    gap: 4px;
    padding: 4px;
    border: 1px solid rgba(255, 255, 255, 0.14);
    border-radius: 13px;
    background: rgba(4, 20, 39, 0.35);
    backdrop-filter: blur(12px);
  }

  .lang-btn {
    min-width: 46px;
    height: 36px;
    padding: 0 10px;
    border: 0;
    border-radius: 9px;
    background: transparent;
    color: rgba(255, 255, 255, 0.72);
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.04em;
    cursor: pointer;
    transition: 180ms ease;
  }

  .lang-btn:hover {
    color: #fff;
    background: rgba(255, 255, 255, 0.08);
  }

  .lang-btn.active {
    border-color: transparent;
    background: linear-gradient(135deg, var(--memorial-gold-light), var(--memorial-gold));
    color: #152238;
    box-shadow: 0 6px 16px rgba(214, 168, 75, 0.24);
  }

  .home-hero {
    max-width: 850px;
    margin: 10px auto 0;
    text-align: center;
  }

  .hero-eyebrow,
  .hero-badge {
    width: fit-content;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin: 0 0 18px;
    padding: 8px 13px;
    border: 1px solid rgba(242, 213, 142, 0.22);
    border-radius: 999px;
    background: rgba(214, 168, 75, 0.11);
    color: #f5daa0;
    font-size: 11px;
    font-weight: 850;
    letter-spacing: 0.14em;
    text-transform: uppercase;
  }

  .home-hero .hero-title,
  .hero-title {
    margin: 0;
    color: #fff;
    font-size: clamp(36px, 5vw, 64px);
    font-weight: 820;
    letter-spacing: -0.045em;
    line-height: 1.06;
    text-wrap: balance;
  }

  .hero-subtitle {
    max-width: 650px;
    margin: 20px auto 0;
    color: rgba(255, 255, 255, 0.72);
    font-size: clamp(16px, 2vw, 19px);
    line-height: 1.65;
  }

  .hero-section {
    display: grid;
    grid-template-columns: 188px minmax(0, 1fr);
    align-items: center;
    gap: 38px;
  }

  .hero-avatar {
    position: relative;
    width: 188px;
    height: 188px;
    padding: 5px;
    border-radius: 34px;
    background: linear-gradient(145deg, #f7dda2, #b88028);
    box-shadow: 0 25px 55px rgba(0, 0, 0, 0.28);
  }

  .hero-avatar::after {
    content: "";
    position: absolute;
    inset: 10px;
    border: 1px solid rgba(255, 255, 255, 0.34);
    border-radius: 25px;
    pointer-events: none;
  }

  .hero-avatar img,
  .hero-avatar .avatar-placeholder {
    width: 100%;
    height: 100%;
    border-radius: 29px;
    object-fit: cover;
  }

  .avatar-placeholder,
  .image-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(145deg, #e8eef5, #d7e0eb);
    color: #8b99aa;
  }

  .hero-content {
    min-width: 0;
  }

  .hero-content .hero-title {
    font-size: clamp(38px, 5vw, 60px);
  }

  .hero-info {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 24px;
  }

  .hero-info span {
    min-height: 38px;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 9px 12px;
    border: 1px solid rgba(255, 255, 255, 0.13);
    border-radius: 11px;
    background: rgba(255, 255, 255, 0.07);
    color: rgba(255, 255, 255, 0.85);
    font-size: 13px;
    font-weight: 650;
    backdrop-filter: blur(10px);
  }

  main.container {
    position: relative;
    z-index: 2;
  }

  .memorials-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 24px;
    margin: -72px 0 0;
    padding-bottom: 12px;
  }

  .memorial-card {
    min-width: 0;
    overflow: hidden;
    border: 1px solid rgba(217, 226, 237, 0.92);
    border-radius: 24px;
    background: rgba(255, 255, 255, 0.98);
    box-shadow: var(--memorial-shadow);
    transition: transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease;
    animation: memorial-rise 520ms both;
  }

  .memorial-card:nth-child(2) { animation-delay: 60ms; }
  .memorial-card:nth-child(3) { animation-delay: 120ms; }
  .memorial-card:nth-child(4) { animation-delay: 180ms; }

  .memorial-card:hover {
    transform: translateY(-6px);
    border-color: rgba(23, 74, 126, 0.22);
    box-shadow: 0 24px 60px rgba(7, 26, 51, 0.15);
  }

  .memorial-image {
    position: relative;
    height: 310px;
    overflow: hidden;
    background: #dfe7ef;
  }

  .memorial-image img,
  .memorial-image .image-placeholder {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 650ms cubic-bezier(.2,.7,.2,1);
  }

  .memorial-card:hover .memorial-image img {
    transform: scale(1.035);
  }

  .image-overlay {
    position: absolute;
    inset: 0;
    background:
      linear-gradient(180deg, rgba(5, 18, 35, 0.02) 20%, rgba(5, 18, 35, 0.9) 100%);
  }

  .image-caption {
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    padding: 52px 22px 21px;
    color: #fff;
  }

  .image-caption h2 {
    margin: 0 0 8px;
    font-size: 24px;
    font-weight: 800;
    letter-spacing: -0.025em;
    line-height: 1.15;
  }

  .image-caption div {
    display: flex;
    align-items: center;
    gap: 6px;
    color: rgba(255, 255, 255, 0.76);
    font-size: 13px;
  }

  .memorial-info {
    padding: 19px 20px 20px;
  }

  .memorial-dates {
    min-height: 42px;
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0 0 15px;
    padding-bottom: 15px;
    border-bottom: 1px solid #edf1f5;
    color: #526176;
    font-size: 13px;
    font-weight: 650;
    line-height: 1.45;
  }

  .memorial-stats {
    display: flex;
    gap: 8px;
    margin: 0 0 17px;
  }

  .memorial-stats span {
    min-height: 34px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 10px;
    border-radius: 10px;
    background: #f2f5f8;
    color: #667388;
    font-size: 12px;
    font-weight: 750;
  }

  .detail-btn,
  .submit-btn {
    min-height: 48px;
    width: 100%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 9px;
    padding: 12px 18px;
    border: 1px solid #0c315c;
    border-radius: 13px;
    background: linear-gradient(135deg, #0a2445, #124a7e);
    box-shadow: 0 10px 22px rgba(11, 45, 85, 0.18);
    color: #fff;
    font-size: 14px;
    font-weight: 800;
    cursor: pointer;
    transition: 180ms ease;
  }

  .detail-btn:hover,
  .submit-btn:hover {
    background: linear-gradient(135deg, #0d315b, #17609d);
    box-shadow: 0 13px 28px rgba(11, 45, 85, 0.25);
    transform: translateY(-1px);
  }

  .detail-btn:disabled,
  .submit-btn:disabled {
    opacity: 0.58;
    cursor: not-allowed;
    transform: none;
  }

  .stats-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
    margin: -55px 0 22px;
  }

  .stat-card {
    min-width: 0;
    min-height: 108px;
    display: flex;
    align-items: center;
    gap: 15px;
    padding: 20px;
    border: 1px solid rgba(219, 227, 237, 0.94);
    border-radius: 19px;
    background: rgba(255, 255, 255, 0.97);
    box-shadow: var(--memorial-shadow-soft);
    backdrop-filter: blur(14px);
  }

  .stat-icon {
    width: 50px;
    height: 50px;
    flex: 0 0 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 15px;
    background: linear-gradient(145deg, #fff8e7, #f3e0b1);
    color: #9a6b1d;
  }

  .stat-value {
    color: var(--memorial-navy);
    font-size: 28px;
    font-weight: 850;
    letter-spacing: -0.035em;
    line-height: 1;
  }

  .stat-label {
    margin-top: 6px;
    color: var(--memorial-muted);
    font-size: 12px;
    font-weight: 750;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .family-note {
    position: relative;
    overflow: hidden;
    margin: 0 0 22px;
    padding: 23px 26px 23px 30px;
    border: 1px solid #eadcb8;
    border-left: 4px solid var(--memorial-gold);
    border-radius: 18px;
    background: linear-gradient(120deg, #fffdf7, #faf3df);
    box-shadow: 0 8px 24px rgba(122, 84, 20, 0.06);
  }

  .family-note::after {
    content: "“";
    position: absolute;
    top: -20px;
    right: 22px;
    color: rgba(172, 123, 39, 0.09);
    font-family: Georgia, serif;
    font-size: 112px;
    line-height: 1;
  }

  .family-note-label {
    position: relative;
    z-index: 1;
    margin-bottom: 8px;
    color: #98691e;
    font-size: 11px;
    font-weight: 850;
    letter-spacing: 0.13em;
    text-transform: uppercase;
  }

  .family-note-text {
    position: relative;
    z-index: 1;
    color: #5e4a27;
    font-family: Georgia, "Times New Roman", serif;
    font-size: 17px;
    font-style: italic;
    line-height: 1.7;
  }

  .tabs-nav {
    position: sticky;
    top: 12px;
    z-index: 20;
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 7px;
    margin: 0 0 22px;
    padding: 7px;
    border: 1px solid rgba(216, 225, 236, 0.92);
    border-radius: 17px;
    background: rgba(255, 255, 255, 0.9);
    box-shadow: 0 11px 30px rgba(7, 26, 51, 0.09);
    backdrop-filter: blur(18px);
  }

  .tab-btn {
    min-width: 0;
    min-height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 9px 12px;
    border: 0;
    border-radius: 11px;
    background: transparent;
    box-shadow: none;
    color: #5a687c;
    font-size: 13px;
    font-weight: 780;
    cursor: pointer;
    transition: 180ms ease;
  }

  .tab-btn:hover {
    background: #f0f4f8;
    color: #173b63;
  }

  .tab-btn.active {
    background: linear-gradient(135deg, #0a2445, #174f85);
    box-shadow: 0 8px 19px rgba(11, 45, 85, 0.2);
    color: #fff;
  }

  .section {
    animation: section-enter 320ms ease both;
  }

  .stories-grid,
  .religious-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 18px;
  }

  .card,
  .location-card,
  .condolence-form,
  .condolence-card {
    border: 1px solid var(--memorial-line);
    background: var(--memorial-surface);
    box-shadow: var(--memorial-shadow-soft);
  }

  .card {
    min-width: 0;
    margin: 0;
    padding: 25px;
    border-radius: 19px;
  }

  .card-header {
    display: flex;
    align-items: center;
    gap: 13px;
    margin: 0 0 18px;
  }

  .card-icon {
    width: 43px;
    height: 43px;
    flex: 0 0 43px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 13px;
    background: linear-gradient(145deg, #fff7e5, #f2dfb3);
    color: #9e6e20;
  }

  .card-header h3,
  .condolence-form h3 {
    margin: 0;
    color: var(--memorial-navy);
    font-size: 18px;
    font-weight: 820;
    letter-spacing: -0.02em;
    line-height: 1.3;
  }

  .card-text {
    margin: 0;
    color: #556277;
    font-size: 15px;
    line-height: 1.78;
    white-space: pre-line;
  }

  .read-more {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin: 17px 0 0;
    padding: 0;
    border: 0;
    background: transparent;
    color: #15558d;
    font-size: 13px;
    font-weight: 800;
    cursor: pointer;
  }

  .religious-card {
    text-align: left;
  }

  .video-btn {
    width: 100%;
    min-height: 48px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    margin: 0 0 17px;
    padding: 12px 16px;
    border: 0;
    border-radius: 12px;
    background: #b42323;
    color: #fff;
    font-weight: 780;
    cursor: pointer;
    transition: 180ms ease;
  }

  .video-btn:hover {
    background: #951e1e;
    transform: translateY(-1px);
  }

  .gallery-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 18px;
  }

  .gallery-item {
    min-width: 0;
    overflow: hidden;
    border: 1px solid var(--memorial-line);
    border-radius: 18px;
    background: #fff;
    box-shadow: var(--memorial-shadow-soft);
    cursor: pointer;
    transition: 200ms ease;
  }

  .gallery-item:hover {
    transform: translateY(-4px);
    box-shadow: var(--memorial-shadow);
  }

  .gallery-item img {
    width: 100%;
    height: 230px;
    display: block;
    object-fit: cover;
  }

  .gallery-caption {
    padding: 16px;
  }

  .gallery-title {
    color: var(--memorial-navy);
    font-size: 14px;
    font-weight: 800;
  }

  .gallery-desc {
    display: -webkit-box;
    overflow: hidden;
    margin-top: 5px;
    color: var(--memorial-muted);
    font-size: 12px;
    line-height: 1.55;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }

  .location-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
    margin: 20px 0 0;
    padding: 20px 22px;
    border-radius: 18px;
  }

  .location-card .card-header {
    margin: 0;
  }

  .location-link {
    flex: 0 0 auto;
    min-height: 44px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin: 0;
    padding: 10px 15px;
    border-radius: 11px;
    background: #edf4fa;
    color: #124b7e;
    font-size: 13px;
    font-weight: 800;
    text-decoration: none;
  }

  .condolence-layout {
    display: grid;
    grid-template-columns: minmax(300px, 0.8fr) minmax(0, 1.4fr);
    align-items: start;
    gap: 20px;
  }

  .condolence-form {
    position: sticky;
    top: 92px;
    margin: 0;
    padding: 24px;
    border-radius: 19px;
  }

  .condolence-form h3 {
    display: flex;
    align-items: center;
    gap: 9px;
    margin-bottom: 20px;
  }

  .form-input,
  .form-textarea {
    width: 100%;
    margin: 0 0 13px;
    padding: 13px 14px;
    border: 1px solid #d9e1eb;
    border-radius: 12px;
    outline: none;
    background: #f9fbfd;
    color: var(--memorial-ink);
    font-size: 14px;
    line-height: 1.5;
    transition: 160ms ease;
  }

  .form-textarea {
    min-height: 126px;
    resize: vertical;
  }

  .form-input::placeholder,
  .form-textarea::placeholder {
    color: #929dae;
  }

  .form-input:focus,
  .form-textarea:focus {
    border-color: #4b82b5;
    background: #fff;
    box-shadow: 0 0 0 4px rgba(23, 74, 126, 0.1);
  }

  .condolences-list {
    display: flex;
    flex-direction: column;
    gap: 13px;
  }

  .condolence-card {
    display: grid;
    grid-template-columns: 48px minmax(0, 1fr);
    gap: 14px;
    padding: 19px;
    border-radius: 16px;
  }

  .condolence-avatar {
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 14px;
    background: linear-gradient(145deg, #0b2b50, #1b568c);
    color: #fff;
    font-size: 17px;
    font-weight: 850;
  }

  .condolence-content {
    min-width: 0;
  }

  .condolence-name {
    color: var(--memorial-navy);
    font-size: 14px;
    font-weight: 820;
  }

  .condolence-date {
    margin: 3px 0 9px;
    color: #8a95a6;
    font-size: 11px;
    font-weight: 650;
  }

  .condolence-message {
    overflow-wrap: anywhere;
    color: #536176;
    font-size: 14px;
    line-height: 1.65;
  }

  .empty-state {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 220px;
    padding: 38px 24px;
    border: 1px dashed #cdd7e3;
    border-radius: 19px;
    background: rgba(255, 255, 255, 0.74);
    color: #8a97a8;
    text-align: center;
  }

  main.container > .empty-state {
    margin-top: -72px;
    background: #fff;
    box-shadow: var(--memorial-shadow);
  }

  .empty-icon {
    width: 66px;
    height: 66px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 0 14px;
    border-radius: 20px;
    background: #edf2f7;
    color: #8190a3;
  }

  .empty-text {
    color: #708095;
    font-size: 14px;
    font-weight: 720;
  }

  .footer {
    position: relative;
    margin-top: 64px;
    padding: 55px 0 42px;
    overflow: hidden;
    background:
      radial-gradient(circle at 86% 20%, rgba(23, 88, 146, 0.32), transparent 26rem),
      linear-gradient(120deg, #06172e, #0a2d55);
    color: #fff;
  }

  .footer::before {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    height: 3px;
    background: linear-gradient(90deg, transparent, var(--memorial-gold), transparent);
  }

  .footer-grid {
    position: relative;
    z-index: 1;
    display: grid;
    grid-template-columns: 1.25fr 1fr 0.7fr;
    gap: clamp(28px, 5vw, 72px);
  }

  .footer h3 {
    margin: 0 0 16px;
    color: #fff;
    font-size: 16px;
    font-weight: 820;
  }

  .footer-text {
    max-width: 430px;
    margin: 0;
    color: rgba(255, 255, 255, 0.58);
    font-size: 14px;
    line-height: 1.7;
  }

  .footer-line {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin: 0 0 11px;
    color: rgba(255, 255, 255, 0.62);
    font-size: 13px;
    line-height: 1.55;
  }

  .footer-line svg {
    flex: 0 0 auto;
    margin-top: 2px;
    color: var(--memorial-gold-light);
  }

  .footer-line a {
    color: inherit;
    text-decoration: none;
  }

  .footer-line a:hover {
    color: #fff;
  }

  .social-links {
    display: flex;
    flex-wrap: wrap;
    gap: 9px;
  }

  .social-link {
    width: 42px;
    height: 42px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid rgba(255, 255, 255, 0.14);
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.07);
    color: #f4d893;
    transition: 180ms ease;
  }

  .social-link:hover {
    border-color: rgba(242, 213, 142, 0.35);
    background: rgba(214, 168, 75, 0.15);
    transform: translateY(-2px);
  }

  .modal-overlay {
    position: fixed;
    inset: 0;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 28px;
    background: rgba(3, 12, 24, 0.9);
    backdrop-filter: blur(12px);
    cursor: pointer;
    animation: fade-in 180ms ease both;
  }

  .modal-content {
    position: relative;
    max-width: 94vw;
    max-height: 88vh;
    cursor: default;
  }

  .modal-close {
    position: absolute;
    top: -48px;
    right: 0;
    width: 38px;
    height: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid rgba(255, 255, 255, 0.22);
    border-radius: 11px;
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
    cursor: pointer;
  }

  .modal-iframe {
    width: min(880px, 90vw);
    aspect-ratio: 16 / 9;
    display: block;
    border: 0;
    border-radius: 16px;
    background: #000;
    box-shadow: 0 28px 80px rgba(0, 0, 0, 0.42);
  }

  .modal-img {
    display: block;
    max-width: 92vw;
    max-height: 84vh;
    border-radius: 15px;
    box-shadow: 0 28px 80px rgba(0, 0, 0, 0.42);
  }

  .toast {
    position: fixed;
    bottom: max(24px, env(safe-area-inset-bottom));
    left: 50%;
    z-index: 1100;
    max-width: calc(100vw - 28px);
    padding: 13px 18px;
    border: 1px solid rgba(255, 255, 255, 0.16);
    border-radius: 12px;
    background: #117a59;
    box-shadow: 0 16px 36px rgba(10, 56, 43, 0.28);
    color: #fff;
    font-size: 13px;
    font-weight: 760;
    text-align: center;
    transform: translateX(-50%);
    animation: toast-enter 260ms ease both;
  }

  .loading-container {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 17px;
    background:
      radial-gradient(circle at 72% 18%, rgba(35, 91, 148, 0.35), transparent 25rem),
      linear-gradient(135deg, #06172e, #0b315c);
    color: #fff;
  }

  .loading-spinner {
    width: 52px;
    height: 52px;
    margin: 0;
    border: 3px solid rgba(255, 255, 255, 0.14);
    border-top-color: var(--memorial-gold-light);
    border-radius: 50%;
    animation: spin 820ms linear infinite;
  }

  .loading-text {
    color: rgba(255, 255, 255, 0.76);
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.04em;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  @keyframes memorial-rise {
    from { opacity: 0; transform: translateY(16px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @keyframes section-enter {
    from { opacity: 0; transform: translateY(7px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @keyframes fade-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes toast-enter {
    from { opacity: 0; transform: translate(-50%, 12px); }
    to { opacity: 1; transform: translate(-50%, 0); }
  }

  @media (max-width: 1024px) {
    .memorials-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .gallery-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .footer-grid {
      grid-template-columns: 1.2fr 1fr;
    }

    .footer-grid > :last-child {
      grid-column: 1 / -1;
    }
  }

  @media (max-width: 720px) {
    .container {
      width: min(100% - 28px, 1280px);
    }

    .home-header {
      min-height: 390px;
      padding: 18px 0 96px;
    }

    .detail-header {
      padding: 18px 0 82px;
    }

    .header-top {
      margin-bottom: 38px;
    }

    .back-button {
      min-height: 42px;
      padding: 9px 13px;
      font-size: 13px;
    }

    .lang-btn {
      min-width: 39px;
      height: 34px;
      padding: 0 7px;
      font-size: 11px;
    }

    .home-hero {
      margin-top: 30px;
    }

    .hero-eyebrow,
    .hero-badge {
      margin-bottom: 14px;
      font-size: 10px;
    }

    .home-hero .hero-title,
    .hero-content .hero-title {
      font-size: clamp(32px, 10vw, 44px);
    }

    .hero-subtitle {
      margin-top: 16px;
      font-size: 15px;
      line-height: 1.6;
    }

    .hero-section {
      grid-template-columns: 1fr;
      justify-items: center;
      gap: 24px;
      text-align: center;
    }

    .hero-avatar {
      width: 142px;
      height: 142px;
      border-radius: 27px;
    }

    .hero-avatar img,
    .hero-avatar .avatar-placeholder {
      border-radius: 22px;
    }

    .hero-avatar::after {
      border-radius: 18px;
    }

    .hero-info {
      justify-content: center;
      margin-top: 18px;
    }

    .hero-info span {
      min-height: 36px;
      font-size: 12px;
    }

    .memorials-grid {
      grid-template-columns: 1fr;
      gap: 17px;
      margin-top: -55px;
    }

    .memorial-card {
      border-radius: 20px;
    }

    .memorial-image {
      height: clamp(260px, 86vw, 360px);
    }

    .image-caption {
      padding: 48px 18px 18px;
    }

    .image-caption h2 {
      font-size: 22px;
    }

    .memorial-info {
      padding: 17px;
    }

    .stats-grid {
      gap: 10px;
      margin-top: -43px;
    }

    .stat-card {
      min-height: 90px;
      gap: 10px;
      padding: 14px;
      border-radius: 16px;
    }

    .stat-icon {
      width: 42px;
      height: 42px;
      flex-basis: 42px;
      border-radius: 12px;
    }

    .stat-value {
      font-size: 23px;
    }

    .stat-label {
      font-size: 10px;
    }

    .family-note {
      padding: 20px 20px 20px 23px;
      border-radius: 15px;
    }

    .family-note-text {
      font-size: 15px;
    }

    .tabs-nav {
      top: 7px;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 6px;
      padding: 6px;
      border-radius: 15px;
    }

    .tab-btn {
      min-height: 43px;
      gap: 6px;
      padding: 8px;
      font-size: 11px;
      line-height: 1.2;
    }

    .stories-grid,
    .religious-grid,
    .gallery-grid,
    .condolence-layout {
      grid-template-columns: 1fr;
    }

    .stories-grid,
    .religious-grid,
    .gallery-grid {
      gap: 13px;
    }

    .card {
      padding: 19px;
      border-radius: 16px;
    }

    .card-header {
      gap: 11px;
      margin-bottom: 15px;
    }

    .card-header h3,
    .condolence-form h3 {
      font-size: 16px;
    }

    .card-text {
      font-size: 14px;
      line-height: 1.72;
    }

    .gallery-item img {
      height: clamp(220px, 70vw, 330px);
    }

    .location-card {
      align-items: stretch;
      flex-direction: column;
      padding: 17px;
    }

    .location-link {
      width: 100%;
    }

    .condolence-layout {
      gap: 13px;
    }

    .condolence-form {
      position: static;
      padding: 19px;
      border-radius: 16px;
    }

    .condolence-card {
      grid-template-columns: 42px minmax(0, 1fr);
      gap: 11px;
      padding: 16px;
    }

    .condolence-avatar {
      width: 42px;
      height: 42px;
      border-radius: 12px;
    }

    main.container > .empty-state {
      margin-top: -55px;
    }

    .footer {
      margin-top: 45px;
      padding: 42px 0 34px;
    }

    .footer-grid,
    .footer-grid > :last-child {
      grid-template-columns: 1fr;
      grid-column: auto;
      gap: 26px;
      text-align: left;
    }

    .footer-line,
    .social-links {
      justify-content: flex-start;
    }

    .modal-overlay {
      padding: 14px;
    }

    .modal-iframe {
      width: calc(100vw - 28px);
    }
  }

  @media (max-width: 380px) {
    .container {
      width: min(100% - 20px, 1280px);
    }

    .back-button span {
      display: none;
    }

    .stats-grid {
      grid-template-columns: 1fr;
    }

    .stat-card {
      min-height: 76px;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    *,
    *::before,
    *::after {
      scroll-behavior: auto !important;
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
    }
  }
`;

export default function PublicCemetery() {
  const { kurumId } = useParams();

  const [lang, setLang] = useState(detectLang());
  const TT = TEXT[lang];

  const [loading, setLoading] = useState(true);
  const [memorials, setMemorials] = useState([]);
  const [contact, setContact] = useState({});
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [activeTab, setActiveTab] = useState("life");
  const [expanded, setExpanded] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [videoModal, setVideoModal] = useState(null);

  const [senderName, setSenderName] = useState("");
  const [senderMessage, setSenderMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState("");

  useEffect(() => {
    if (!kurumId) return;

    setLoading(true);

    Promise.all([
      fetch(`${API_BASE}/public/cemetery/list/${kurumId}`).then((r) =>
        r.json(),
      ),
      fetch(`${API_ILETISIM}?kurum_id=${kurumId}&lang=${lang}`)
        .then((r) => r.json())
        .catch(() => ({ data: {} })),
    ])
      .then(([cemeteryRes, contactRes]) => {
        setMemorials(cemeteryRes?.ok ? cemeteryRes.data || [] : []);
        setContact(contactRes?.data || {});
      })
      .catch((err) => {
        console.error("Public cemetery load error:", err);
      })
      .finally(() => setLoading(false));
  }, [kurumId, lang]);

  const openDetail = async (id) => {
    setDetailLoading(true);
    setActiveTab("life");
    setExpanded(null);

    try {
      const res = await fetch(`${API_BASE}/public/cemetery/memorial/${id}`);
      const data = await res.json();

      if (data.ok) {
        setSelected(data.data);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        alert(data.message || TT.error);
      }
    } catch (err) {
      console.error("Detail error:", err);
      alert(TT.error);
    } finally {
      setDetailLoading(false);
    }
  };

  const sendCondolence = async () => {
    if (!senderName.trim() || !senderMessage.trim()) {
      alert(TT.name + " / " + TT.message);
      return;
    }

    setSending(true);

    try {
      const res = await fetch(`${API_BASE}/public/cemetery/condolences`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          memorial_id: selected?.memorial?.id,
          sender_name: senderName.trim(),
          message: senderMessage.trim(),
        }),
      });

      const data = await res.json();

      if (data.ok) {
        setToast(data.message || TT.sent);
        setSenderName("");
        setSenderMessage("");

        if (data.data?.status === "approved") {
          setSelected((prev) => ({
            ...prev,
            condolences: [data.data, ...(prev.condolences || [])],
          }));
        }

        setTimeout(() => setToast(""), 3000);
      } else {
        alert(data.message || TT.error);
      }
    } catch (err) {
      console.error("Condolence error:", err);
      alert(TT.error);
    } finally {
      setSending(false);
    }
  };

  const m = selected?.memorial;
  const lifeStories = useMemo(() => safeArray(m?.life_stories), [m]);
  const religiousItems = useMemo(() => safeArray(m?.religious_items), [m]);
  const memoriesJson = useMemo(() => safeArray(m?.memories), [m]);
  const galleryTable = selected?.gallery || [];
  const condolences = selected?.condolences || [];

  const allGallery = useMemo(() => {
    const fromMemories = memoriesJson.map((x, index) => ({
      id: `memory-${index}`,
      title: x.title || x.baslik || "",
      description: x.description || x.aciklama || "",
      image: resolveImage(x.image_url || x.image || x.media_url || x.url),
      type: "memory",
    }));

    const fromGallery = galleryTable.map((x) => ({
      id: `gallery-${x.id}`,
      title: x.title || "",
      description: x.description || "",
      image: resolveImage(x.media_url || x.image_url),
      type: x.media_type || "image",
    }));

    return [...fromGallery, ...fromMemories].filter((x) => x.image);
  }, [galleryTable, memoriesJson]);

  if (loading) {
    return (
      <div className="loading-container">
        <style>{APP_STYLES}</style>
        <div className="loading-spinner" />
        <div className="loading-text">{TT.loading}</div>
      </div>
    );
  }

  if (selected && m) {
    return (
      <div className="app-container">
        <style>{`
          /* GLOBAL RESET */
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }

          .app-container {
            min-height: 100vh;
            background: #f8fafc;
            font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
            color: #1e293b;
          }

          .container {
            max-width: 1200px;
            margin: 0 auto;
            padding: 0 24px;
          }

          /* HEADERS */
          .home-header {
            background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%);
            color: white;
            padding: 32px 0 80px;
          }

          .detail-header {
            background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
            color: white;
            padding: 24px 0 48px;
          }

          .header-top {
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex-wrap: wrap;
            gap: 16px;
            margin-bottom: 32px;
          }

          .back-button {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            background: rgba(255,255,255,0.1);
            border: 1px solid rgba(255,255,255,0.2);
            border-radius: 999px;
            padding: 8px 20px;
            color: white;
            cursor: pointer;
            text-decoration: none;
            font-weight: 600;
            transition: all 0.2s;
          }

          .back-button:hover {
            background: rgba(255,255,255,0.2);
          }

          .lang-switch {
            display: flex;
            gap: 8px;
          }

          .lang-btn {
            border: 1px solid rgba(255,255,255,0.3);
            background: rgba(255,255,255,0.1);
            color: white;
            border-radius: 999px;
            padding: 6px 14px;
            font-weight: 700;
            cursor: pointer;
            transition: all 0.2s;
          }

          .lang-btn.active {
            background: #f5b642;
            color: #0f172a;
            border-color: #f5b642;
          }

          /* HERO SECTION */
          .hero-section {
            display: flex;
            align-items: center;
            gap: 32px;
            flex-wrap: wrap;
          }

          .hero-avatar {
            flex-shrink: 0;
            width: 180px;
            height: 180px;
            border-radius: 50%;
            background: linear-gradient(135deg, #f5b642, #fbbf24);
            padding: 4px;
          }

          .hero-avatar img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            border-radius: 50%;
          }

          .avatar-placeholder {
            width: 100%;
            height: 100%;
            border-radius: 50%;
            background: #f1f5f9;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #94a3b8;
          }

          .hero-badge {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            background: rgba(245,182,66,0.15);
            padding: 6px 14px;
            border-radius: 999px;
            font-size: 13px;
            font-weight: 600;
            color: #fbbf24;
            margin-bottom: 16px;
          }

          .hero-title {
            font-size: 42px;
            font-weight: 800;
            margin-bottom: 12px;
            line-height: 1.2;
          }

          .hero-info {
            display: flex;
            flex-wrap: wrap;
            gap: 12px;
            font-size: 14px;
          }

          .hero-info span {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            background: rgba(255,255,255,0.1);
            padding: 6px 14px;
            border-radius: 999px;
          }

          .home-hero {
            text-align: center;
            max-width: 700px;
            margin: 40px auto 0;
          }

          .home-hero .hero-title {
            font-size: 48px;
            margin-bottom: 16px;
          }

          .hero-subtitle {
            font-size: 18px;
            opacity: 0.9;
          }

          /* STATS - PROFESSIONAL 2-LI GRID */
          .stats-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 16px;
            margin: -40px 0 24px;
          }

          .stat-card {
            background: white;
            border-radius: 20px;
            padding: 16px;
            display: flex;
            align-items: center;
            gap: 12px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.05);
          }

          .stat-icon {
            width: 48px;
            height: 48px;
            background: #fef3c7;
            border-radius: 16px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 24px;
            color: #f5b642;
          }

          .stat-value {
            font-size: 24px;
            font-weight: 800;
            color: #0f172a;
          }

          .stat-label {
            font-size: 12px;
            color: #64748b;
          }

          /* FAMILY NOTE */
          .family-note {
            background: linear-gradient(135deg, #fffbeb, #fef3c7);
            border-left: 5px solid #f5b642;
            border-radius: 20px;
            padding: 20px;
            margin-bottom: 24px;
          }

          .family-note-label {
            font-size: 12px;
            font-weight: 700;
            color: #b45309;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin-bottom: 8px;
          }

          .family-note-text {
            font-size: 16px;
            line-height: 1.6;
            color: #78350f;
            font-style: italic;
          }

          /* TABS - MOBILE 2'LI GRID */
          .tabs-nav {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 10px;
            margin-bottom: 24px;
            position: sticky;
            top: 0;
            background: #f8fafc;
            padding: 12px 0;
            z-index: 10;
          }

          .tab-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            padding: 10px;
            border: none;
            border-radius: 999px;
            background: white;
            color: #334155;
            font-weight: 700;
            cursor: pointer;
            transition: all 0.2s;
            box-shadow: 0 2px 6px rgba(0,0,0,0.05);
          }

          .tab-btn.active {
            background: #0f172a;
            color: white;
          }

          /* CARDS */
          .card {
            background: white;
            border-radius: 24px;
            padding: 20px;
            margin-bottom: 20px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.05);
          }

          .card-header {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-bottom: 16px;
          }

          .card-icon {
            width: 44px;
            height: 44px;
            background: #fef3c7;
            border-radius: 16px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #f5b642;
          }

          .card-header h3 {
            font-size: 20px;
            margin: 0;
          }

          .card-text {
            color: #475569;
            line-height: 1.7;
            margin: 0;
          }

          .read-more {
            margin-top: 12px;
            background: none;
            border: none;
            color: #f5b642;
            font-weight: 600;
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            gap: 4px;
          }

          /* RELIGIOUS - VIDEO BUTTON (MODAL ILE) */
          .religious-card {
            text-align: center;
          }

          .video-btn {
            display: inline-flex;
            align-items: center;
            gap: 10px;
            background: #ff0000;
            color: white;
            padding: 12px 24px;
            border-radius: 999px;
            text-decoration: none;
            font-weight: 700;
            margin: 16px 0;
            transition: transform 0.2s, background 0.2s;
            border: none;
            cursor: pointer;
          }

          .video-btn:hover {
            background: #cc0000;
            transform: scale(1.02);
          }

          /* GALLERY */
          .gallery-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
            gap: 20px;
          }

          .gallery-item {
            background: white;
            border-radius: 20px;
            overflow: hidden;
            cursor: pointer;
            transition: transform 0.2s;
            box-shadow: 0 4px 12px rgba(0,0,0,0.05);
          }

          .gallery-item:hover {
            transform: translateY(-4px);
          }

          .gallery-item img {
            width: 100%;
            height: 200px;
            object-fit: cover;
          }

          .gallery-caption {
            padding: 12px;
          }

          .gallery-title {
            font-weight: 700;
            margin-bottom: 4px;
          }

          .gallery-desc {
            font-size: 13px;
            color: #64748b;
          }

          /* LOCATION */
          .location-card {
            background: white;
            border-radius: 24px;
            padding: 20px;
            margin-top: 20px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.05);
          }

          .location-link {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            background: #f5b642;
            color: #0f172a;
            padding: 10px 20px;
            border-radius: 999px;
            text-decoration: none;
            font-weight: 700;
            margin-top: 12px;
          }

          /* CONDOLENCE FORM */
          .condolence-form {
            background: white;
            border-radius: 24px;
            padding: 24px;
            margin-bottom: 24px;
          }

          .condolence-form h3 {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 20px;
          }

          .form-input, .form-textarea {
            width: 100%;
            padding: 14px 16px;
            border: 1px solid #e2e8f0;
            border-radius: 16px;
            font-size: 15px;
            margin-bottom: 16px;
            font-family: inherit;
          }

          .form-textarea {
            resize: vertical;
          }

          .form-input:focus, .form-textarea:focus {
            outline: none;
            border-color: #f5b642;
          }

          .submit-btn {
            width: 100%;
            background: #0f172a;
            color: white;
            border: none;
            border-radius: 999px;
            padding: 14px;
            font-weight: 700;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 10px;
            cursor: pointer;
            transition: background 0.2s;
          }

          .submit-btn:hover {
            background: #1e293b;
          }

          .submit-btn:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          /* CONDOLENCES LIST */
          .condolences-list {
            display: flex;
            flex-direction: column;
            gap: 16px;
          }

          .condolence-card {
            background: white;
            border-radius: 20px;
            padding: 20px;
            display: flex;
            gap: 16px;
          }

          .condolence-avatar {
            width: 48px;
            height: 48px;
            background: #f5b642;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 800;
            font-size: 20px;
            color: #0f172a;
            flex-shrink: 0;
          }

          .condolence-content {
            flex: 1;
          }

          .condolence-name {
            font-weight: 800;
            margin-bottom: 4px;
          }

          .condolence-date {
            font-size: 12px;
            color: #94a3b8;
            margin-bottom: 8px;
          }

          .condolence-message {
            color: #475569;
            line-height: 1.6;
          }

          /* MEMORIALS GRID (HOME) */
          .memorials-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
            gap: 24px;
            margin-top: -40px;
          }

          .memorial-card {
            background: white;
            border-radius: 28px;
            overflow: hidden;
            box-shadow: 0 8px 24px rgba(0,0,0,0.08);
            transition: transform 0.2s;
          }

          .memorial-card:hover {
            transform: translateY(-4px);
          }

          .memorial-image {
            position: relative;
            height: 250px;
            overflow: hidden;
          }

          .memorial-image img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }

          .image-placeholder {
            width: 100%;
            height: 100%;
            background: #f1f5f9;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #94a3b8;
          }

          .image-overlay {
            position: absolute;
            inset: 0;
            background: linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.7));
          }

          .image-caption {
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            padding: 20px;
            color: white;
          }

          .image-caption h2 {
            margin: 0 0 6px;
            font-size: 22px;
          }

          .image-caption div {
            font-size: 13px;
            display: flex;
            align-items: center;
            gap: 4px;
          }

          .memorial-info {
            padding: 18px;
          }

          .memorial-dates {
            display: flex;
            align-items: center;
            gap: 6px;
            color: #64748b;
            font-size: 13px;
            font-weight: 500;
            margin-bottom: 12px;
          }

          .memorial-stats {
            display: flex;
            gap: 16px;
            font-size: 13px;
            color: #64748b;
            margin-bottom: 16px;
          }

          .memorial-stats span {
            display: inline-flex;
            align-items: center;
            gap: 4px;
          }

          .detail-btn {
            width: 100%;
            background: #0f172a;
            color: white;
            border: none;
            border-radius: 999px;
            padding: 12px;
            font-weight: 700;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            cursor: pointer;
            transition: background 0.2s;
          }

          .detail-btn:hover {
            background: #1e293b;
          }

          .detail-btn:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          /* FOOTER */
       .footer { 
  background: linear-gradient(
    135deg,
    #0f172a 0%,
    #1e3a8a 45%,
    #2563eb 100%
  );
  color: white; 
  padding: 48px 0 32px; 
  margin-top: 48px;
}

           .footer-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
            gap: 32px;
          }

          .footer h3 {
            margin-bottom: 16px;
            font-size: 18px;
          }

          .footer-text {
            color: #94a3b8;
            line-height: 1.6;
          }

          .footer-line {
            display: flex;
            align-items: center;
            gap: 10px;
            color: #94a3b8;
            margin-bottom: 12px;
          }

          .footer-line a {
            color: #94a3b8;
            text-decoration: none;
          }

          .footer-line a:hover {
            color: #f5b642;
          }

          .social-links {
            display: flex;
            gap: 12px;
            flex-wrap: wrap;
          }

          .social-link {
            width: 42px;
            height: 42px;
            background: #f5b642;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #0f172a;
            transition: transform 0.2s;
          }

          .social-link:hover {
            transform: scale(1.1);
          }

          /* EMPTY STATE */
          .empty-state {
            background: white;
            border-radius: 28px;
            padding: 60px 32px;
            text-align: center;
            color: #94a3b8;
          }

          .empty-icon {
            margin-bottom: 16px;
          }

          .empty-text {
            font-weight: 600;
          }

          /* TOAST */
          .toast {
            position: fixed;
            bottom: 30px;
            left: 50%;
            transform: translateX(-50%);
            background: #10b981;
            color: white;
            padding: 12px 24px;
            border-radius: 999px;
            font-weight: 600;
            z-index: 1000;
            box-shadow: 0 8px 20px rgba(0,0,0,0.2);
          }

          /* LIGHTBOX & MODAL */
          .modal-overlay {
            position: fixed;
            inset: 0;
            background: rgba(0,0,0,0.85);
            z-index: 1000;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 24px;
            cursor: pointer;
          }

          .modal-content {
            position: relative;
            max-width: 90vw;
            max-height: 85vh;
          }

          .modal-close {
            position: absolute;
            top: -40px;
            right: 0;
            background: rgba(0,0,0,0.5);
            border: none;
            color: white;
            width: 36px;
            height: 36px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            font-size: 20px;
          }

          .modal-close:hover {
            background: rgba(0,0,0,0.7);
          }

          .modal-iframe {
            width: 80vw;
            height: 45vw;
            max-width: 800px;
            max-height: 450px;
            border: none;
            border-radius: 16px;
          }

          .modal-img {
            max-width: 90vw;
            max-height: 85vh;
            border-radius: 16px;
          }

          /* LOADING */
          .loading-container {
            min-height: 100vh;
            background: linear-gradient(135deg, #0f172a, #1e293b);
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            color: white;
          }

          .loading-spinner {
            width: 50px;
            height: 50px;
            border: 4px solid rgba(255,255,255,0.2);
            border-top-color: #f5b642;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin-bottom: 20px;
          }

          @keyframes spin {
            to { transform: rotate(360deg); }
          }

          .loading-text {
            font-size: 18px;
            font-weight: 600;
          }

          /* RESPONSIVE */
          @media (max-width: 768px) {
            .container {
              padding: 0 16px;
            }

            .hero-section {
              flex-direction: column;
              text-align: center;
            }

            .hero-info {
              justify-content: center;
            }

            .hero-title {
              font-size: 32px;
            }

            .home-hero .hero-title {
              font-size: 36px;
            }

            .gallery-grid {
              grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
            }

            .condolence-card {
              flex-direction: column;
              align-items: center;
              text-align: center;
            }

            .footer-grid {
              text-align: center;
            }

            .footer-line {
              justify-content: center;
            }

            .social-links {
              justify-content: center;
            }

            .modal-iframe {
              width: 95vw;
              height: 55vw;
            }
          }
        `}</style>

        <style>{APP_STYLES}</style>

        <header className="detail-header">
          <div className="container">
            <div className="header-top">
              <button onClick={() => setSelected(null)} className="back-button">
                <ArrowLeft size={18} /> <span>{TT.backHome}</span>
              </button>
              <div className="lang-switch">
                {["tr", "en", "de"].map((l) => (
                  <button
                    key={l}
                    onClick={() => setLang(l)}
                    className={`lang-btn ${lang === l ? "active" : ""}`}
                  >
                    {l.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>

            <div className="hero-section">
              <div className="hero-avatar">
                {m.photo_url ? (
                  <img src={resolveImage(m.photo_url)} alt={m.fullname} />
                ) : (
                  <div className="avatar-placeholder">
                    <User size={80} />
                  </div>
                )}
              </div>
              <div className="hero-content">
                <div className="hero-badge">
                  <Sparkles size={16} /> {TEXT[lang].title}
                </div>
                <h1 className="hero-title">{m.fullname}</h1>
                <div className="hero-info">
                  <span>
                    <Calendar size={16} />{" "}
                    {formatDate(m.birth_date_text, m.birth_date_picker, lang)} -{" "}
                    {formatDate(m.death_date_text, m.death_date_picker, lang)}
                  </span>
                  {m.cemetery_name && (
                    <span>
                      <MapPin size={16} /> {m.cemetery_name}
                    </span>
                  )}
                  {m.grave_no && (
                    <span>
                      {TT.graveNo}: {m.grave_no}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>

        <main className="container">
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">
                <Eye size={24} />
              </div>
              <div>
                <div className="stat-value">{m.view_count || 0}</div>
                <div className="stat-label">{TT.views}</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">
                <MessageCircle size={24} />
              </div>
              <div>
                <div className="stat-value">
                  {m.condolences_count || condolences.length}
                </div>
                <div className="stat-label">{TT.messages}</div>
              </div>
            </div>
          </div>

          {m.family_note && (
            <div className="family-note">
              <div className="family-note-label">{TT.familyNote}</div>
              <div className="family-note-text">“{m.family_note}”</div>
            </div>
          )}

          <nav className="tabs-nav">
            <button
              onClick={() => setActiveTab("life")}
              className={`tab-btn ${activeTab === "life" ? "active" : ""}`}
            >
              <BookOpen size={17} /> {TT.life}
            </button>
            <button
              onClick={() => setActiveTab("religious")}
              className={`tab-btn ${activeTab === "religious" ? "active" : ""}`}
            >
              <Youtube size={17} /> {TT.religious}
            </button>
            <button
              onClick={() => setActiveTab("gallery")}
              className={`tab-btn ${activeTab === "gallery" ? "active" : ""}`}
            >
              <ImageIcon size={17} /> {TT.gallery}
            </button>
            <button
              onClick={() => setActiveTab("condolences")}
              className={`tab-btn ${activeTab === "condolences" ? "active" : ""}`}
            >
              <Heart size={17} /> {TT.condolences}
            </button>
          </nav>

          {activeTab === "life" && (
            <div className="section">
              {lifeStories.length === 0 ? (
                <Empty text={TT.emptyLife} icon={<BookOpen size={44} />} />
              ) : (
                <div className="stories-grid">
                  {lifeStories.map((story, i) => {
                    const content =
                      story.content || story.text || story.description || "";
                    const title = story.title || story.baslik || TT.life;
                    const isLong = content.length > 360;
                    const showFull = expanded === i;

                    return (
                      <article key={i} className="card">
                        <div className="card-header">
                          <div className="card-icon">
                            <BookOpen size={21} />
                          </div>
                          <h3>{title}</h3>
                        </div>
                        <p className="card-text">
                          {showFull || !isLong
                            ? content
                            : `${content.slice(0, 360)}...`}
                        </p>
                        {isLong && (
                          <button
                            onClick={() => setExpanded(showFull ? null : i)}
                            className="read-more"
                          >
                            {showFull ? TT.readLess : TT.readMore}{" "}
                            <ChevronRight size={16} />
                          </button>
                        )}
                      </article>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === "religious" && (
            <div className="section">
              {religiousItems.length === 0 ? (
                <Empty text={TT.emptyReligious} icon={<Youtube size={44} />} />
              ) : (
                <div className="religious-grid">
                  {religiousItems.map((item, i) => {
                    const embedUrl = getYouTubeEmbedUrl(
                      item.link || item.url || item.video_url,
                    );
                    const title =
                      item.title || item.baslik || item.type || TT.religious;

                    return (
                      <article key={i} className="card religious-card">
                        <div className="card-header">
                          <div className="card-icon">
                            <PlayCircle size={23} />
                          </div>
                          <h3>{title}</h3>
                        </div>

                        {embedUrl && (
                          <button
                            onClick={() => setVideoModal(embedUrl)}
                            className="video-btn"
                          >
                            <Youtube size={24} />
                            <span>{TT.watchOnYouTube}</span>
                          </button>
                        )}

                        {(item.text || item.description || item.aciklama) && (
                          <p className="card-text">
                            {item.text || item.description || item.aciklama}
                          </p>
                        )}
                      </article>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === "gallery" && (
            <div className="section">
              {allGallery.length === 0 ? (
                <Empty text={TT.emptyGallery} icon={<ImageIcon size={44} />} />
              ) : (
                <div className="gallery-grid">
                  {allGallery.map((item) => (
                    <div
                      key={item.id}
                      onClick={() => setLightbox(item.image)}
                      className="gallery-item"
                    >
                      <img src={item.image} alt={item.title} />
                      {(item.title || item.description) && (
                        <div className="gallery-caption">
                          {item.title && (
                            <div className="gallery-title">{item.title}</div>
                          )}
                          {item.description && (
                            <div className="gallery-desc">
                              {item.description}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {m.location_address && activeTab !== "condolences" && (
            <div className="location-card">
              <div className="card-header">
                <div className="card-icon">
                  <MapPin size={22} />
                </div>
                <h3>{TT.location}</h3>
              </div>
              <a
                href={m.location_address}
                target="_blank"
                rel="noreferrer"
                className="location-link"
              >
                {TT.mapOpen} <MapPin size={17} />
              </a>
            </div>
          )}

          {activeTab === "condolences" && (
            <div className="section">
              <div className="condolence-layout">
                <div className="condolence-form">
                  <h3>
                    <Heart size={22} /> {TT.sendMessage}
                  </h3>
                  <input
                    value={senderName}
                    onChange={(e) => setSenderName(e.target.value)}
                    placeholder={TT.name}
                    className="form-input"
                  />
                  <textarea
                    value={senderMessage}
                    onChange={(e) => setSenderMessage(e.target.value)}
                    placeholder={TT.messagePlaceholder}
                    rows={4}
                    className="form-textarea"
                  />
                  <button
                    onClick={sendCondolence}
                    disabled={sending}
                    className="submit-btn"
                  >
                    <Send size={18} /> {TT.send}
                  </button>
                </div>

                <div className="condolences-list">
                  {condolences.length === 0 ? (
                    <Empty
                      text={TT.emptyCondolence}
                      icon={<MessageCircle size={44} />}
                    />
                  ) : (
                    condolences.map((c) => (
                      <div key={c.id} className="condolence-card">
                        <div className="condolence-avatar">
                          {c.sender_name?.slice(0, 1)?.toUpperCase() || "?"}
                        </div>
                        <div className="condolence-content">
                          <div className="condolence-name">{c.sender_name}</div>
                          <div className="condolence-date">
                            {c.created_at
                              ? new Date(c.created_at).toLocaleDateString()
                              : ""}
                          </div>
                          <div className="condolence-message">{c.message}</div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </main>

        <Footer contact={contact} TT={TT} />

        {toast && <Toast text={toast} />}

        {/* Lightbox for Images */}
        {lightbox && (
          <div onClick={() => setLightbox(null)} className="modal-overlay">
            <div onClick={(e) => e.stopPropagation()} className="modal-content">
              <button onClick={() => setLightbox(null)} className="modal-close">
                <X size={18} />
              </button>
              <img src={lightbox} alt="" className="modal-img" />
            </div>
          </div>
        )}

        {/* Modal for YouTube Videos */}
        {videoModal && (
          <div onClick={() => setVideoModal(null)} className="modal-overlay">
            <div onClick={(e) => e.stopPropagation()} className="modal-content">
              <button
                onClick={() => setVideoModal(null)}
                className="modal-close"
              >
                <X size={18} />
              </button>
              <iframe
                src={videoModal}
                className="modal-iframe"
                allowFullScreen
                title="YouTube Video Player"
                allow="autoplay; encrypted-media"
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="app-container">
      <style>{`
        * { margin: 0; padding: 0; box-sizing: border-box; }
        .app-container { min-height: 100vh; background: #f8fafc; font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif; color: #1e293b; }
        .container { max-width: 1200px; margin: 0 auto; padding: 0 24px; }
        .home-header { background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%); color: white; padding: 32px 0 80px; }
        .header-top { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 16px; margin-bottom: 32px; }
        .back-button { display: inline-flex; align-items: center; gap: 8px; background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.2); border-radius: 999px; padding: 8px 20px; color: white; cursor: pointer; text-decoration: none; font-weight: 600; transition: all 0.2s; }
        .back-button:hover { background: rgba(255,255,255,0.2); }
        .lang-switch { display: flex; gap: 8px; }
        .lang-btn { border: 1px solid rgba(255,255,255,0.3); background: rgba(255,255,255,0.1); color: white; border-radius: 999px; padding: 6px 14px; font-weight: 700; cursor: pointer; transition: all 0.2s; }
        .lang-btn.active { background: #f5b642; color: #0f172a; border-color: #f5b642; }
        .home-hero { text-align: center; max-width: 700px; margin: 40px auto 0; }
        .hero-title { font-size: 48px; font-weight: 800; margin-bottom: 16px; line-height: 1.2; }
        .hero-subtitle { font-size: 18px; opacity: 0.9; }
        .memorials-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 24px; margin-top: -40px; }
        .memorial-card { background: white; border-radius: 28px; overflow: hidden; box-shadow: 0 8px 24px rgba(0,0,0,0.08); transition: transform 0.2s; }
        .memorial-card:hover { transform: translateY(-4px); }
        .memorial-image { position: relative; height: 250px; overflow: hidden; }
        .memorial-image img { width: 100%; height: 100%; object-fit: cover; }
        .image-placeholder { width: 100%; height: 100%; background: #f1f5f9; display: flex; align-items: center; justify-content: center; color: #94a3b8; }
        .image-overlay { position: absolute; inset: 0; background: linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.7)); }
        .image-caption { position: absolute; bottom: 0; left: 0; right: 0; padding: 20px; color: white; }
        .image-caption h2 { margin: 0 0 6px; font-size: 22px; }
        .image-caption div { font-size: 13px; display: flex; align-items: center; gap: 4px; }
        .memorial-info { padding: 18px; }
        .memorial-dates { display: flex; align-items: center; gap: 6px; color: #64748b; font-size: 13px; font-weight: 500; margin-bottom: 12px; }
        .memorial-stats { display: flex; gap: 16px; font-size: 13px; color: #64748b; margin-bottom: 16px; }
        .memorial-stats span { display: inline-flex; align-items: center; gap: 4px; }
        .detail-btn { width: 100%; background: #0f172a; color: white; border: none; border-radius: 999px; padding: 12px; font-weight: 700; display: flex; align-items: center; justify-content: center; gap: 8px; cursor: pointer; transition: background 0.2s; }
        .detail-btn:hover { background: #1e293b; }
        .detail-btn:disabled { opacity: 0.6; cursor: not-allowed; }
        .empty-state { background: white; border-radius: 28px; padding: 60px 32px; text-align: center; color: #94a3b8; }
        .empty-icon { margin-bottom: 16px; }
        .empty-text { font-weight: 600; }
  .footer { 
  background: linear-gradient(
    135deg,
    #0f172a 0%,
    #1e3a8a 45%,
    #2563eb 100%
  );
  color: white; 
  padding: 48px 0 32px; 
  margin-top: 48px;
}
        .footer-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 32px; }
        .footer h3 { margin-bottom: 16px; font-size: 18px; }
        .footer-text { color: #94a3b8; line-height: 1.6; }
        .footer-line { display: flex; align-items: center; gap: 10px; color: #94a3b8; margin-bottom: 12px; }
        .footer-line a { color: #94a3b8; text-decoration: none; }
        .footer-line a:hover { color: #f5b642; }
        .social-links { display: flex; gap: 12px; flex-wrap: wrap; }
        .social-link { width: 42px; height: 42px; background: #f5b642; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #0f172a; transition: transform 0.2s; }
        .social-link:hover { transform: scale(1.1); }
        @media (max-width: 768px) {
          .container { padding: 0 16px; }
          .hero-title { font-size: 36px; }
          .memorials-grid { grid-template-columns: 1fr; }
          .footer-grid { text-align: center; }
          .footer-line { justify-content: center; }
          .social-links { justify-content: center; }
        }
      `}</style>

      <style>{APP_STYLES}</style>

      <header className="home-header">
        <div className="container">
          <div className="header-top">
            <Link to="/" className="back-button">
              <Home size={17} /> <span>{TT.backHome}</span>
            </Link>
            <div className="lang-switch">
              {["tr", "en", "de"].map((l) => (
                <button
                  key={l}
                  onClick={() => setLang(l)}
                  className={`lang-btn ${lang === l ? "active" : ""}`}
                >
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          <div className="home-hero">
            <div className="hero-eyebrow">
              <Sparkles size={14} /> {TT.title}
            </div>
            <h1 className="hero-title">{contact.kurum_adi || TT.title}</h1>
            <p className="hero-subtitle">{TT.subtitle}</p>
          </div>
        </div>
      </header>

      <main className="container">
        {memorials.length === 0 ? (
          <Empty text={TT.noData} icon={<Heart size={52} />} />
        ) : (
          <div className="memorials-grid">
            {memorials.map((item) => (
              <article key={item.id} className="memorial-card">
                <div className="memorial-image">
                  {item.photo_url ? (
                    <img
                      src={resolveImage(item.photo_url)}
                      alt={item.fullname}
                    />
                  ) : (
                    <div className="image-placeholder">
                      <User size={70} />
                    </div>
                  )}
                  <div className="image-overlay" />
                  <div className="image-caption">
                    <h2>{item.fullname}</h2>
                    {item.cemetery_name && (
                      <div>
                        <MapPin size={15} /> {item.cemetery_name}
                      </div>
                    )}
                  </div>
                </div>

                <div className="memorial-info">
                  <div className="memorial-dates">
                    <Calendar size={16} />
                    {formatDate(
                      item.birth_date_text,
                      item.birth_date_picker,
                      lang,
                    )}{" "}
                    -{" "}
                    {formatDate(
                      item.death_date_text,
                      item.death_date_picker,
                      lang,
                    )}
                  </div>

                  <div className="memorial-stats">
                    <span>
                      <Eye size={14} /> {item.view_count || 0}
                    </span>
                    <span>
                      <MessageCircle size={14} /> {item.condolences_count || 0}
                    </span>
                  </div>

                  <button
                    onClick={() => openDetail(item.id)}
                    disabled={detailLoading}
                    className="detail-btn"
                  >
                    {TT.detail} <ChevronRight size={17} />
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </main>

      <Footer contact={contact} TT={TT} />
    </div>
  );
}

function Footer({ contact, TT }) {
  return (
    <footer className="footer">
      <div className="container footer-grid">
        <div>
          <h3>{contact.kurum_adi || TT.title}</h3>
          <p className="footer-text">{TT.subtitle}</p>
        </div>

        <div>
          <h3>{TT.contact}</h3>
          {(contact.adres || contact.adres_tr || contact.address) && (
            <p className="footer-line">
              <MapPin size={16} />{" "}
              {contact.adres || contact.adres_tr || contact.address}
            </p>
          )}
          {contact.telefon && (
            <p className="footer-line">
              <Phone size={16} />{" "}
              <a href={`tel:${contact.telefon}`}>{contact.telefon}</a>
            </p>
          )}
          {contact.email && (
            <p className="footer-line">
              <Mail size={16} />{" "}
              <a href={`mailto:${contact.email}`}>{contact.email}</a>
            </p>
          )}
        </div>

        <div>
          <h3>{TT.follow}</h3>
          <div className="social-links">
            {contact.instagram && (
              <Social href={contact.instagram} icon={<Instagram size={20} />} />
            )}
            {contact.facebook && (
              <Social href={contact.facebook} icon={<Facebook size={20} />} />
            )}
            {contact.youtube && (
              <Social href={contact.youtube} icon={<Youtube size={20} />} />
            )}
            {contact.website && (
              <Social href={contact.website} icon={<Globe size={20} />} />
            )}
          </div>
        </div>
      </div>
    </footer>
  );
}

function Social({ href, icon }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" className="social-link">
      {icon}
    </a>
  );
}

function Empty({ text, icon }) {
  return (
    <div className="empty-state">
      <div className="empty-icon">{icon}</div>
      <div className="empty-text">{text}</div>
    </div>
  );
}

function Toast({ text }) {
  return <div className="toast">{text}</div>;
}
